import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import InputField from 'components/InputField';
import TextAreaField from 'components/TextAreaField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import validations from 'validations';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import AuthContext from 'providers/Auth/Auth.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import EmailField from 'components/EmailField';
import ContactNumberField from 'components/ContactNumberField';
import api from 'api';
import { CONTACT_US_ROUTE } from 'data/global';

import './ContactUsForm.styles.scss';
import './ContactUsForm.styles.responsive.scss';

export type ContactUsFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  contactNumber?: string;
  message: string;
};

type ContactUsFormProps = {
  className?: string;
};

const ContactUsForm: React.FC<ContactUsFormProps> = (props) => {
  const { className } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const { navigateBack } = useContext(AuthContext);
  const { currentUser } = useContext(CurrentUserContext);

  const initialValues = useMemo(() => {
    if (!currentUser) return {};

    const { firstName, lastName, email, contactNumber } = currentUser;

    return { firstName, lastName, email, contactNumber };
  }, [currentUser]);

  const classes = classNames('contact-us-form', className);

  const commonValidations = useValidations();

  const { t } = useTranslation();

  const onSubmit = useCallback(
    async (values: ContactUsFormValues) => {
      try {
        await api.general.contactUs(values);
        popSuccess(t('contactUsSuccessMsg'));
        navigateBack({
          replace: true,
          excludeRoutes: [CONTACT_US_ROUTE],
        });
      } catch (e) {
        popServerError(e);
      }
    },
    [navigateBack, popServerError, popSuccess, t],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ submitting, invalid, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          <div className="fields">
            <Field
              name="firstName"
              component={InputField}
              label={t('firstName')}
              required
              validate={commonValidations.name}
            />
            <Field
              name="lastName"
              component={InputField}
              label={t('lastName')}
              required
              validate={commonValidations.name}
            />
          </div>

          <div className="fields">
            <EmailField required />
            <ContactNumberField />
          </div>

          <Field
            name="message"
            component={TextAreaField}
            label={t('message')}
            rows={4}
            required
            validate={validations.required()}
          />

          <div className="actions">
            <Button type="submit" disabled={submitting || invalid}>
              {t('submit')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default ContactUsForm;
